if (!customElements.get('sticky-tab-nav')) {
  customElements.define('sticky-tab-nav', class StickyTabNav extends HTMLElement {
    connectedCallback() {
      this._isDesignMode = this.dataset.designMode === 'true';
      this._analyticsEnabled = this.dataset.analytics === 'true';
      this._abortController = new AbortController();

      this._collectTabs();
      if (!this._tabs.length) {
        this.hidden = true;
        return;
      }

      this._tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          this._activateTab(tab);
          if (tab.dataset.tabGroup) this._scrollToTabContent(tab.dataset.tabGroup);
        }, { signal: this._abortController.signal });
        tab.addEventListener('keydown', e => this._handleKeydown(e), { signal: this._abortController.signal });
      });

      window.addEventListener('hashchange', () => this._activateFromHash(), { signal: this._abortController.signal });

      if (this._isDesignMode) {
        document.addEventListener('shopify:section:load', () => this._refresh(), { signal: this._abortController.signal });
        document.addEventListener('shopify:section:unload', () => this._refresh(), { signal: this._abortController.signal });
      }

      this._activateFromHash() || this._activateTab(this._tabs[0], false);
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    _collectTabs() {
      const allTabs = Array.from(this.querySelectorAll('[role="tab"]'));

      if (this._isDesignMode) {
        // In the editor, show every tab regardless of whether the target section
        // is currently on the page — merchants need to configure them.
        this._tabs = allTabs;
        this._tabs.forEach(t => t.removeAttribute('hidden'));
        return;
      }

      this._tabs = allTabs.filter(tab => {
        const groupId = tab.dataset.tabGroup;
        if (!groupId) return true;
        const hasContent = !!document.querySelector(`.tab-group-marker[data-tab-group="${CSS.escape(groupId)}"]`);
        if (!hasContent) {
          tab.setAttribute('hidden', '');
          tab.setAttribute('aria-hidden', 'true');
          return false;
        }
        tab.removeAttribute('hidden');
        tab.removeAttribute('aria-hidden');
        return true;
      });
    }

    _refresh() {
      this._collectTabs();
      if (!this._tabs.length) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      const current = this._tabs.find(t => t.getAttribute('aria-selected') === 'true');
      this._activateTab(current || this._tabs[0], false);
    }

    /**
     * @param {HTMLElement} tab
     * @param {boolean} [updateHash=true]
     */
    _activateTab(tab, updateHash = true) {
      if (!tab) return;

      const wasAlreadySelected = tab.getAttribute('aria-selected') === 'true'
        && tab.classList.contains('sticky-tab-nav__tab--active');

      this._tabs.forEach(t => {
        const isTarget = t === tab;
        t.setAttribute('aria-selected', isTarget ? 'true' : 'false');
        t.setAttribute('tabindex', isTarget ? '0' : '-1');
        t.classList.toggle('sticky-tab-nav__tab--active', isTarget);
      });

      // Always re-apply visibility — first activation must override the
      // self-hide style emitted by tab-group-init even if Liquid pre-marked
      // this tab as aria-selected.
      const activeGroupId = tab.dataset.tabGroup;
      if (activeGroupId) {
        StickyTabNav._showOnlyGroup(activeGroupId);
      }

      // Skip side-effects (hash, scroll, analytics) for true no-op clicks.
      if (wasAlreadySelected) return;

      if (updateHash) {
        const hash = tab.dataset.tabHash;
        if (hash) {
          history.replaceState(null, '', `#${hash}`);
        }
      }

      this._scrollTabIntoView(tab);
      this._dispatchAnalytics(tab);
    }

    /** @param {KeyboardEvent} e */
    _handleKeydown(e) {
      const { key } = e;
      const visibleTabs = this._tabs.filter(t => !t.hasAttribute('hidden'));
      const currentIndex = visibleTabs.indexOf(document.activeElement);
      let nextIndex = -1;

      if (key === 'ArrowRight' || key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (currentIndex + 1) % visibleTabs.length;
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
      } else if (key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (key === 'End') {
        e.preventDefault();
        nextIndex = visibleTabs.length - 1;
      } else if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        const active = document.activeElement;
        this._activateTab(active);
        if (active.dataset.tabGroup) this._scrollToTabContent(active.dataset.tabGroup);
        return;
      }

      if (nextIndex >= 0 && visibleTabs[nextIndex]) {
        const nextTab = visibleTabs[nextIndex];
        nextTab.focus();
        this._activateTab(nextTab);
        if (nextTab.dataset.tabGroup) this._scrollToTabContent(nextTab.dataset.tabGroup);
      }
    }

    /** @returns {boolean} true if a matching tab was activated */
    _activateFromHash() {
      const hash = window.location.hash.replace('#', '');
      if (!hash) return false;

      const matchingTab = this._tabs.find(t => t.dataset.tabHash === hash);
      if (matchingTab) {
        this._activateTab(matchingTab, false);
        return true;
      }
      return false;
    }

    /**
     * Slide the tab bar so the active tab moves toward the centre — this keeps
     * the next (or previous) adjacent tab peeking into view on mobile, giving
     * users a visual hint that more tabs are available to select.
     * Browsers clamp at the scrollable bounds, so first/last tabs still
     * naturally show their one available neighbour.
     * @param {HTMLElement} tab
     */
    _scrollTabIntoView(tab) {
      tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    /** Scroll the page so the active tab's content section sits flush below the sticky bar. */
    _scrollToTabContent(groupId) {
      const marker = document.querySelector(`.tab-group-marker[data-tab-group="${CSS.escape(groupId)}"]`);
      if (!marker) return;
      const wrapper = marker.closest('[id^="shopify-section-"]');
      if (!wrapper) return;

      requestAnimationFrame(() => {
        const navBottom = this.getBoundingClientRect().bottom;
        const delta = wrapper.getBoundingClientRect().top - navBottom;
        if (Math.abs(delta) > 1) {
          window.scrollBy({ top: delta, behavior: 'smooth' });
        }
      });
    }

    /** @param {HTMLElement} tab */
    _dispatchAnalytics(tab) {
      if (!this._analyticsEnabled) return;
      const detail = {
        sectionId: this.dataset.sectionId,
        tabIndex: tab.dataset.tabIndex,
        tabTitle: tab.textContent.trim(),
        tabHash: tab.dataset.tabHash,
        tabGroupId: tab.dataset.tabGroup
      };

      document.dispatchEvent(new CustomEvent('sticky-tab-nav:tab-changed', {
        bubbles: true,
        detail
      }));

      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({
          event: 'sticky_tab_nav_change',
          tab_title: detail.tabTitle,
          tab_group_id: detail.tabGroupId,
          tab_index: detail.tabIndex,
          section_id: detail.sectionId
        });
      }
    }

    /**
     * Reveal every Shopify section wrapper whose tab-group-marker matches
     * activeGroupId; hide all others that participate in the tab system.
     * Scoped to .tab-group-marker so we don't accidentally match the tab
     * buttons themselves (which also carry data-tab-group).
     * @param {string} activeGroupId
     */
    static _showOnlyGroup(activeGroupId) {
      document.querySelectorAll('.tab-group-marker[data-tab-group]').forEach(marker => {
        const wrapper = marker.closest('[id^="shopify-section-"]');
        if (!wrapper) return;
        if (marker.dataset.tabGroup === activeGroupId) {
          wrapper.setAttribute('data-tab-group-active', '');
        } else {
          wrapper.removeAttribute('data-tab-group-active');
        }
      });
    }

  });
}
